<template>
	<div>
		<v-navigation-drawer
			id="hirsch-navigation"
			:rail="mini"
			app
			permanent
			:class="{
				'orange-drawer': showProductionBanner && isProductionEnv && isSuperUser,
				'has-banner-offset': !isProductionEnv && !isPreviewActive,
				'has-banner-preview-offset': !isProductionEnv && isPreviewActive,
				'has-preview-offset': isProductionEnv && isPreviewActive,
			}"
		>
			<div v-if="isLoggedIn" data-testid="loggedIn" class="navigation-container">
				<v-list>
					<v-list-item class="px-2">
						<v-list-item-media>
							<v-img :src="user.avatar"></v-img>
						</v-list-item-media>
					</v-list-item>
					<v-list-item link v-if="!mini">
						<v-list-item-title class="text-h6 font-weight-medium"> {{ user.firstName }} {{ user.lastName }} </v-list-item-title>
						<v-list-item-subtitle class="font-weight-bold mb-1">{{ currentTenant }}</v-list-item-subtitle>
						<v-list-item-subtitle v-if="user.superuser" class="mb-1"> Superuser</v-list-item-subtitle>
						<v-list-item-subtitle v-for="role in user.roles" :key="role.name" class="mb-1"> {{ role.name }} </v-list-item-subtitle>

						<div>
							<div>
								<v-chip
									v-if="socketService.isConnected"
									class="font-weight-bold custom-tiny-chip px-4 mt-6 ml-2 mb-6"
									size="x-small"
									color="green"
									text-color="white"
									variant="flat"
								>
									{{ $t('status_connected') }}
								</v-chip>
								<v-chip v-else class="ma-2" size="x-small" color="red" text-color="white">
									{{ $t('status_disconnected') }}
								</v-chip>
							</div>
						</div>
					</v-list-item>
					<v-list-item link v-if="mini">
						<div>
							<v-icon v-if="socketService.isConnected" size="x-small" color="green" :title="this.$t('status_connected')"
								>mdi-checkbox-blank-circle
							</v-icon>
							<v-icon v-else size="small" color="red" :title="this.$t('status_disconnected')"> mdi-checkbox-blank-circle </v-icon>
						</div>
					</v-list-item>
				</v-list>

				<v-divider></v-divider>

				<v-list nav density="compact" open-strategy="multiple" v-if="user.permissions">
					<NavigationItem v-if="user.permissions['users.read']" icon="mdi-account-group" path="/users" :text="$t('navigation_users')" />
					<NavigationItem v-if="user.permissions['groups.read']" icon="mdi-account-group" path="/userGroups" :text="$t('navigation_userGroups')" />
					<NavigationItem
						v-if="user.permissions['roles.read']"
						icon="mdi-account-details"
						path="/roles"
						:text="$t('navigation_roles')"
						data-testid="roles"
					/>

					<v-list-group value="statistics">
						<template v-slot:activator="{ props }">
							<v-list-item v-bind="props" class="list-group">
								<template v-slot:prepend>
									<v-icon color="primary">mdi-chart-line</v-icon>
								</template>
								<v-list-item-title>{{ $t('navigation_statistics') }}</v-list-item-title>
							</v-list-item>
						</template>

						<NavigationItem icon="mdi-cash" path="/extraPay" :text="$t('navigation_extraPay')" />
						<NavigationItem icon="mdi-chart-bar" path="/statisticsOverview" :text="$t('navigation_statisticsOverview')" />
						<NavigationItem icon="mdi-timetable" path="/absenceStatistics" :text="$t('title_absenceStatistics')" />
						<NavigationItem icon="mdi-graph" path="/scores" :text="$t('navigation_scores')" />
						<NavigationItem icon="mdi-timetable" path="/worktimesummary" :text="$t('navigation_worktimeSummary')" />
						<NavigationItem :icon="extraWorkReportIcon" path="/extraWorkReport" :text="$t('aria.extra_work_report')" />
						<NavigationItem icon="mdi-alert" path="/violations" :text="$t('title_violations')" />
					</v-list-group>

					<NavigationItem
						v-if="user.permissions['qualification.read']"
						icon="mdi-badge-account"
						path="/qualification"
						:text="$t('navigation_qualification')"
					/>

					<NavigationItem v-if="user.permissions['plugins.edit']" icon="mdi-puzzle" path="/plugin" :text="$t('navigation_plugin')" />
					<!-- <NavigationItem
						v-if="user.permissions['days.read'] && user.superuser"
						icon="mdi-calendar-month"
						path="/calendar"
						:text="$t('navigation_calendar')"
					/> -->
					<NavigationItem v-if="user.permissions['duties.read']" icon="mdi-table" path="/planning" :text="$t('navigation_planning')" />

					<NavigationItem v-if="user.permissions['duties.read']" icon="mdi-lightbulb-on" path="/solution" :text="$t('navigation_solution')" />

					<PluginHook v-if="user.permissions['rotation.read']" location="RotationNavigationItem"></PluginHook>

					<template v-if="user.permissions['core.read']">
						<v-list-item class="list-group" @click="coreOpen = !coreOpen">
							<template v-slot:prepend>
								<v-icon color="primary">mdi-brain</v-icon>
							</template>
							<v-list-item-title>{{ $t('navigation_core') }}</v-list-item-title>
							<template v-slot:append>
								<v-icon>{{ coreOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
							</template>
						</v-list-item>
						<div v-show="coreOpen" class="core-group-items">
							<NavigationItem path="/core" icon="mdi-chart-box" :text="$t('navigation_dashboard')" />
							<NavigationItem v-if="isSuperUser" path="/basic-fitness" icon="mdi-dumbbell" :text="$t('navigation_basic_fitness')" />
						</div>
					</template>

					<NavigationItem v-if="user.permissions['exchange.read']" icon="mdi-account-switch" path="/dutyExchange" :text="$t('duty_exchange')" />
					<NavigationItem v-if="user.permissions['duties.read']" icon="mdi-text-box-outline" path="/dutyTemplates" :text="$t('duty_templates')" />
					<NavigationItem v-if="user.permissions['apikey.read']" icon="mdi-key-variant" path="/app-api-key" :text="$t('navigation_apikey')" />
					<NavigationItem
						v-if="user.permissions['adapters.read']"
						icon="mdi-access-point"
						path="/adapters/status"
						:text="$t('navigation_adapter_status')"
					/>
					<NavigationItem
						v-if="user.permissions['adapters.edit'] && user.superuser"
						icon="mdi-database-edit"
						path="/adapters/manage"
						:text="$t('navigation_adapter_manage')"
					/>
					<NavigationItem
						v-if="user.superuser"
						icon="mdi-monitor-dashboard"
						path="/performanceMetrics"
						:text="$t('navigation_performance_metrics')"
					/>
					<NavigationItem
						v-if="user.permissions['setup.customization'] || user.permissions['dump.export'] || user.permissions['dump.import']"
						icon="mdi-cog"
						path="/setup"
						:text="$t('navigation_setup')"
					/>
				</v-list>
			</div>
			<div v-else data-testid="notLoggedIn" class="navigation-container">
				<v-list nav density="compact">
					<v-list-item :to="'/login'" :active="currentPath.includes('/login')" density="compact">
						<template v-slot:prepend>
							<v-icon color="primary">mdi-account-group</v-icon>
						</template>
						<v-list-item-title>{{ $t('navigation_login') }}</v-list-item-title>
					</v-list-item>
					<v-list-item :to="'/register'" :active="currentPath.includes('/register')" density="compact" class="auth-nav-item">
						<template v-slot:prepend>
							<v-icon color="primary">mdi-account-multiple</v-icon>
						</template>
						<v-list-item-title>{{ $t('navigation_signup') }}</v-list-item-title>
					</v-list-item>
				</v-list>
			</div>

			<Version class="version" :asIcon="mini"></Version>
		</v-navigation-drawer>
	</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import Version from '../common/version/views/Version.vue';
import extraWorkReportIcon from '@/assets/extra-work-report.svg';
import PluginHook from '../../plugins/common/frontend/PluginHook.vue';
import { socketService } from '../../components/socket/SocketService';
import NavigationItem from './NavigationItem.vue';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';

export default {
	name: 'Navigation',
	components: {
		Version,
		PluginHook,
		NavigationItem,
	},
	props: {
		mini: Boolean,
	},
	setup() {
		const { isActive: isPreviewActive } = useDutyChangePreview();
		return { isPreviewActive };
	},
	data: () => ({
		coreOpen: false,
		extraWorkReportIcon,
	}),
	computed: {
		...mapState({
			user: (state) => state.auth.user,
			loadingApp: (state) => state.lifecycle.loading,
			tenants: (state) => state.auth.tenants,
			signupAllowed: (state) => state.auth.signupAllowed,
			selectedTenant: (state) => state.auth.selectedTenant,
		}),
		...mapGetters({
			isLoggedIn: 'auth/isLoggedIn',
			isSuperUser: 'auth/isSuperUser',
			showProductionBanner: 'profile/showProductionBanner',
		}),
		currentTenant() {
			if (this.tenants && this.tenants.length > 0) {
				let localTenant = window.localStorage.getItem('tenant');
				let tenant = this.tenants.find((tenant) => {
					return tenant.value === localTenant;
				});

				return tenant?.text || '';
			}

			return '';
		},
		isProductionEnv() {
			return import.meta.env.VITE_STAGE === 'production';
		},
		socketService() {
			return socketService;
		},
		currentPath() {
			return this.$route.path ?? '';
		},
	},
	methods: {
		...mapActions({
			loadSignupAllowed: 'auth/loadSignupAllowed',
			loadTenants: 'auth/loadTenants',
		}),
	},
	async beforeMount() {
		if (this.tenants.length === 0) {
			await this.loadTenants();
		}

		this.loadSignupAllowed();
	},
};
</script>

<style scoped>
:deep(#hirsch-navigation .v-navigation-drawer__content) {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: center;
}

:deep(#hirsch-navigation .v-list--nav .v-list-item) {
	width: 100%;
	display: flex;
	padding: 0 16px;
}
:deep(#hirsch-navigation .v-list-group__items) {
	--indent-padding: 5px;
}

:deep(#hirsch-navigation .v-list-group__items .v-list-item) {
	padding-inline-start: 36px !important;
}

:deep(#hirsch-navigation .v-list-group__items .v-list-item__spacer) {
	width: 16px;
}

:deep(.v-list-item.list-group > .v-list-item__append) {
	margin-left: auto;
}

:deep(.v-list-item__prepend > .v-icon) {
	opacity: 1;
}

:deep(.v-list) {
	padding-inline: 0;
}

.core-group-items :deep(.v-list-item) {
	padding-inline-start: 36px !important;
}

.version {
	width: 100%;
	display: flex;
	justify-content: center;
	padding: 0px 16px;
	padding-bottom: 8px;
}
.orange-drawer {
	background-color: #ff9248 !important;
}

.navigation-container {
	width: 100%;
}

.v-list {
	padding-inline: 0;
}

.v-list-item__prepend > .v-icon {
	opacity: 1;
}

:deep(.v-list-item.list-group > .v-list-item__append) {
	margin-left: auto;
}

.custom-tiny-chip {
	height: 18px !important;
	font-size: 0.65rem !important;
	padding: 0 8px !important;
	font-weight: 700;
	letter-spacing: 0.02em;
	text-transform: capitalize;
}

:deep(.v-chip__content) {
	line-height: 1;
}

[data-testid='notLoggedIn'] .v-list-item {
	width: auto !important;
	margin-left: 45px;
	margin-right: 45px;
	border-radius: 4px;
	background-color: #ffffff !important;
}

.v-navigation-drawer--rail [data-testid='notLoggedIn'] .v-list-item {
	margin: 0;
}

.v-navigation-drawer:not(.v-navigation-drawer--rail) [data-testid='notLoggedIn'] .v-list-item-title {
	overflow: visible !important;
	text-overflow: unset !important;
	white-space: nowrap !important;
}
.auth-nav-item {
	margin-bottom: 8px;
	padding: 8px 16px !important;
	min-height: 40px !important;
}
.auth-nav-item .v-list-item-title {
	font-size: 14px;
	font-weight: 500;
	color: #333;
}

.auth-nav-item.v-list-item--active {
	background-color: #eeeeee !important;
}

.auth-nav-item.v-list-item--active .v-list-item__overlay {
	opacity: 0 !important;
}

.auth-nav-item.v-list-item--active .v-list-item-title {
	color: #333 !important;
}

#hirsch-navigation {
	transition:
		top 0.2s ease,
		height 0.2s ease;
}
#hirsch-navigation.has-banner-offset {
	top: 20px !important;
	height: calc(100% - 20px) !important;
}
#hirsch-navigation.has-banner-preview-offset {
	top: 40px !important;
	height: calc(100% - 40px) !important;
}
#hirsch-navigation.has-preview-offset {
	top: 40px !important;
	height: calc(100% - 40px) !important;
}
</style>
